/**
 * HomePage
 * Vertikale Galerie - Skulpturen gestapelt, Kamera fährt runter
 */

import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGLTF } from '@react-three/drei'
import { Scene } from '@/components/three/Scene'
import { DevSettings, useDevSettings } from '@/components/ui/DevSettings'
import { useArtworks } from '@/hooks/useArtworks'
import { useTheme } from '@/hooks/useTheme'
import { mediaUrl } from '@/utils/mediaUrl'
import { Seo, ANNA_PERSON_LD } from '@/components/Seo'
import { SITE_BASE, SITE_NAME, SITE_TAGLINE } from '@/config/site'
import { useT, STRINGS } from '@/context/LanguageContext'
import './HomePage.css'

export function HomePage() {
  const navigate = useNavigate()
  const { getFeaturedArtworks, loading } = useArtworks()
  const { isLight } = useTheme()
  const t = useT()
  const [currentIndex, setCurrentIndex] = useState(0)
  const { settings: glowSettings, updateSettings: setGlowSettings, defaultSettings } = useDevSettings(isLight)

  const featured = getFeaturedArtworks()
  const currentArtwork = featured[currentIndex] || null

  // Cinematic fade-overlay during long camera transits — wheel/swipe only
  // jumps ±1 so the camera glides smoothly between adjacent sculptures,
  // but dot-clicks can jump arbitrarily (e.g. 0 → 5) and the GSAP camera
  // sweep then flashes through every intermediate model in 2 s, which
  // reads as a glitchy "reload". This adds a brief dark dissolve so the
  // visitor sees a clean cut from old framing to new framing instead.
  const prevIndexRef = useRef(currentIndex)
  const [inTransit, setInTransit] = useState(false)
  useEffect(() => {
    const delta = Math.abs(currentIndex - prevIndexRef.current)
    prevIndexRef.current = currentIndex
    if (delta <= 1) return
    setInTransit(true)
    const timer = setTimeout(() => setInTransit(false), 600)
    return () => clearTimeout(timer)
  }, [currentIndex])

  // Preload every featured GLB up-front. The Scene mounts all featured
  // sculptures simultaneously, stacked on Y, and pans the camera between
  // them — so disposing a model mid-scroll would break the active render
  // (empty geometry → momentary blank scene before re-load).
  //
  // The original crash (Hermine review #30) was a memory ceiling issue;
  // the GLB-pipeline params (decimate=0.6 + 512px textures, in
  // generate-content-index.cjs) shrunk each model by ~4× — six concurrent
  // models now fit comfortably under the renderer's heap budget. The
  // on-unmount cleanup below still flushes everything when the visitor
  // leaves the HomePage.
  useEffect(() => {
    featured.forEach((artwork) => {
      const url = mediaUrl(artwork.files.glb)
      if (url) useGLTF.preload(url)
    })
  }, [featured])

  // Disable scrolling on HomePage (Canvas needs fixed viewport).
  // On unmount also flush every featured GLB from the cache — we don't
  // want stale models hanging around when the visitor navigates to a
  // category or detail page.
  useEffect(() => {
    document.body.classList.add('no-scroll')
    return () => {
      document.body.classList.remove('no-scroll')
      featured.forEach((artwork) => {
        const url = mediaUrl(artwork.files.glb)
        if (url) useGLTF.clear(url)
      })
    }
  }, [featured])

  // Tap/click the sculpture itself to open its detail page.
  //
  // Until 2026-09-07 only the small title in the bottom-right corner was
  // a link — Alex's report: "Wenn ich auf eine Statue klicke, passiert
  // nichts." Visitors have no way to guess that the caption is the only
  // hit target, so the whole stage is clickable now.
  //
  // The catch: the same surface rotates the sculpture on drag. So a press
  // only counts as a click when the pointer barely moved (< 8 px) and was
  // released quickly (< 500 ms) — anything longer or wider is a rotation
  // and must not navigate. Listening on window rather than the container
  // because the canvas takes pointer capture during a drag, which would
  // otherwise swallow pointerup (same reason the swipe paging below sits
  // on window).
  const artworkRef = useRef(currentArtwork)
  artworkRef.current = currentArtwork
  useEffect(() => {
    const TAP_MAX_DISTANCE = 8
    const TAP_MAX_DURATION = 500
    let start: { x: number; y: number; t: number } | null = null

    const isChrome = (target: EventTarget | null) =>
      target instanceof Element && !!target.closest('a, button, input, .hero-dots')

    const onDown = (e: PointerEvent) => {
      // Ignore presses on the caption link, the dot navigation and the
      // dev overlay — those handle their own clicks.
      start = isChrome(e.target) ? null : { x: e.clientX, y: e.clientY, t: Date.now() }
    }

    const onUp = (e: PointerEvent) => {
      const s = start
      start = null
      const art = artworkRef.current
      if (!s || !art) return
      if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > TAP_MAX_DISTANCE) return
      if (Date.now() - s.t > TAP_MAX_DURATION) return
      navigate(`/werk/${art.categoryId}/${art.slug}`)
    }

    window.addEventListener('pointerdown', onDown)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointerup', onUp)
    }
  }, [navigate])

  // Mouse-wheel paging — next/previous sculpture. Refs avoid the
  // effect re-binding on every index change.
  const currentIndexRef = useRef(currentIndex)
  const featuredLengthRef = useRef(featured.length)
  const wheelBlockedRef = useRef(false)
  currentIndexRef.current = currentIndex
  featuredLengthRef.current = featured.length

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (wheelBlockedRef.current) return
      wheelBlockedRef.current = true
      setTimeout(() => { wheelBlockedRef.current = false }, 1200)

      if (e.deltaY > 0 && currentIndexRef.current < featuredLengthRef.current - 1) {
        setCurrentIndex(prev => prev + 1)
      } else if (e.deltaY < 0 && currentIndexRef.current > 0) {
        setCurrentIndex(prev => prev - 1)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    return () => window.removeEventListener('wheel', handleWheel)
  }, [])

  // Vertical-swipe paging at the window level.
  //
  // The 3D canvas inside <Scene> calls setPointerCapture for its own
  // drag-to-rotate, which steals pointerup from any parent listener —
  // on Android Chrome the previous HomePage pointer handlers therefore
  // never fired. Listen on touchstart/touchend directly on window so we
  // still see the gesture, then only act if the motion was clearly
  // vertical (horizontal stays with the canvas for rotation).
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        touchStartRef.current = null
        return
      }
      const t = e.touches[0]
      touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() }
    }
    const onTouchEnd = (e: TouchEvent) => {
      const start = touchStartRef.current
      touchStartRef.current = null
      if (!start) return
      const t = e.changedTouches[0]
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      // Vertical-dominant swipe ≥50px = page sibling
      if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        if (dy < 0 && currentIndexRef.current < featuredLengthRef.current - 1) {
          setCurrentIndex(prev => prev + 1)
        } else if (dy > 0 && currentIndexRef.current > 0) {
          setCurrentIndex(prev => prev - 1)
        }
      }
    }
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (featured.length === 0) {
    return (
      <div className="error-screen">
        <p>Keine Artworks gefunden</p>
      </div>
    )
  }

  return (
    <main className="home-page">
      <Seo
        title="Skulpturen Portfolio"
        description="3D-Skulpturen Portfolio von Anna Sacher Santana — Bildhauerin und Kinder- und Jugendpsychiaterin. Werke aus der Verbindung von Kunst und Medizin."
        image={mediaUrl(currentArtwork?.files?.glb ? `/content/thumbnails/dark/${currentArtwork.slug}.png` : null)}
        jsonLd={[
          ANNA_PERSON_LD,
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: `${SITE_NAME} — ${SITE_TAGLINE}`,
            url: SITE_BASE,
            inLanguage: 'de',
          },
        ]}
      />
      {/* 3D Scene mit allen Skulpturen. Touch paging is handled at the
          window level via touchstart/touchend (see useEffect above) so
          the canvas's own pointer-capture for drag rotation doesn't
          block it. */}
      <div className="scene-container">
        <Scene
          artworks={featured}
          currentIndex={currentIndex}
          viewMode="hero"
          glowSettings={glowSettings}
        />

        {/* Transit fade — see prevIndexRef effect above. Only active for
            multi-step jumps from dot-click navigation. */}
        <div className={`scene-transit ${inTransit ? 'active' : ''}`} aria-hidden="true" />

        {/* Sculpture title in the bottom-right corner — small, no
            artist subtitle. */}
        {currentArtwork && (
          <div className="hero-caption">
            <Link to={`/werk/${currentArtwork.categoryId}/${currentArtwork.slug}`} className="hero-caption-link">
              <h1 className="hero-title">{currentArtwork.title}</h1>
            </Link>
          </div>
        )}
      </div>

      {featured.length > 1 && (
        <div className="hero-dots vertical">
          {featured.map((_, idx) => (
            <button
              key={idx}
              className={`dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                setCurrentIndex(idx)
              }}
              aria-label={`Artwork ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Developer Settings Overlay (F9 to toggle) */}
      <DevSettings settings={glowSettings} onSettingsChange={setGlowSettings} defaultSettings={defaultSettings} />
    </main>
  )
}
